import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

// Stripe stuff
import { loadStripe } from "@stripe/stripe-js";

import {
  Elements,
  PaymentElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import PurchaseTermsDisclosure, {
  purchaseTermsVariantFor,
} from "./purchaseTermsDisclosure.jsx";

import CircularProgress from "@mui/material/CircularProgress";
import MuiTextField from "@mui/material/TextField";
import { withStyles } from "@mui/styles";

import {
  Button,
  GridContainerWithSpacing as GridContainer,
  GridItemWithSpacing as GridItem,
} from "~/shared/components/MuiComponents.jsx";

import {
  useKlarnaCheckoutExperiment,
  useOneTimePurchaseDisclosure,
  VARIANT_CONTROL,
  VARIANT_TEST,
} from "~/shared/experiments";
import { bugsnagClient } from "~/shared/utils/errorHandling";
import { isConsultationTypeInsomniaInitial } from "~/shared/utils/sharedUtils";
import {
  isStripeElementNotReadyError,
  patientFacingStripeError,
  PAYMENT_GENERIC_ERROR_MESSAGE,
} from "~/shared/utils/stripeJsErrors";
import KickClient from "~/visits/utils/api.js";
import {
  getOrderComplete,
  getPromoCode,
  getVisitDifferentPharmacy,
  getVisitEmail,
  getVisitFullName,
  getVisitID,
  getVisitPhone,
  getVisitProduct,
} from "~/visits/utils/visitCookies.js";
import { useVisitIDRequired } from "~/visits/utils/visitUtils";

import { CheckoutProgress } from "../shared.jsx";
import { checkoutGroupingHash } from "./checkoutErrorGrouping.js";
import CheckoutStatusCard, { StatusButton } from "./checkoutStatusCard.jsx";
import { finishKlarnaCheckout, klarnaReturnURL } from "./klarnaCheckout";
import KlarnaCheckoutReturn, {
  KLARNA_RESUME_MODE,
  KLARNA_RETURN_MODE,
} from "./klarnaCheckoutReturn";
import {
  DoctorGuaranteeSection,
  SelectedSleepMeds,
  SleepConciergeInfo,
  SocialProof,
  WhatHappensNext,
} from "./paymentComponents.jsx";
import {
  checkoutPostData,
  completeCheckoutAndRedirect,
  getStripeOptions,
  orderTotalPriceWithCouponDiscount,
  paymentDetailsFormValidationSchema,
} from "./paymentHelpers.js";
import { recoverSucceededSetupIntent } from "./setupIntentRecovery.js";

import { computeDeviceFingerprint } from "~/visits/utils/deviceFingerprint";

import {
  kickAmaOrange,
  kickLightBlue,
  kickNavy,
} from "~/shared/styles/baseStyles.js";
import consultationStyles, {
  VISIT_MAX_WIDTH,
} from "~/visits/styles/consultationStyles.js";

let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
      betas: ["link_default_integration_beta_1"],
    });
  }
  return stripePromise;
};

// How long Confirm stays disabled waiting for the PaymentElement's `ready`
// event. Without a gate a patient on a slow connection could press a live
// Confirm above an empty card box; Stripe then rejected with IntegrationError
// "could not retrieve data from the specified Element" and the patient saw
// nothing happen. After the timeout the click is let through: either the
// iframe caught up and it works, or Stripe rejects and the retry message below
// is shown. The button is never permanently dead.
export const PAYMENT_ELEMENT_READY_TIMEOUT_MS = 15000;

export const PAYMENT_FORM_LOADING_MESSAGE =
  "The payment form is still loading. Please wait a moment and try again.";
export const PAYMENT_FORM_LOAD_FAILED_MESSAGE =
  "The payment form couldn't load. Check your connection and refresh the page to try again.";
export const PAYMENT_RETRY_MESSAGE =
  "We couldn't reach the payment form. Please wait a moment and press Confirm again, or refresh the page.";

// Hoisted out of PaymentDetails on purpose. A component defined inside another
// component is a brand-new type on every parent render, so every
// setMyCreditCardError / setFormState used to remount this form, tear down the
// PaymentElement iframe and create a second stripe.paymentRequest() (Bugsnag's
// "Frame not initialized." churn, and a lost card form after a declined card).
// It stays mounted unless the patient explicitly starts a new payment method.
export const BillingForm = ({
  classes,
  checkoutSessionID,
  consultationType,
  cardName,
  creditCardError,
  history,
  buyWithInvoice,
  hasConfirmedPaymentMethod = false,
  isChangingPaymentMethod = false,
  // Why the patient is back on the form after a Klarna detour (see
  // klarnaCheckoutReturn.jsx). Informational, so it is not the red error line.
  notice = "",
  changePaymentMethod,
  rememberConfirmedPaymentMethod,
  setFormState,
  setMyCreditCardError,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentRequest, setPaymentRequest] = useState(null); // for apple pay
  const totalDueToday = orderTotalPriceWithCouponDiscount(
    true,
    checkoutSessionID,
    getPromoCode(checkoutSessionID),
  );

  // "loading" | "ready" | "timeout" | "failed" — see PAYMENT_ELEMENT_READY_TIMEOUT_MS.
  const [paymentElementState, setPaymentElementState] = useState("loading");
  // Messages about the form itself (not ready, load failed, retry). Kept as
  // form-local state so showing one never re-renders the parent.
  const [formMessage, setFormMessage] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("card");

  // Only a subscription requires the terms tick, so the schema depends on the order
  // type. See paymentDetailsFormValidationSchema.
  const orderType = getVisitProduct(checkoutSessionID);

  // One-time orders are in an experiment on whether the disclosure panel renders at
  // all. Subscriptions are never eligible -- see useOneTimePurchaseDisclosure.
  const { ready: disclosureReady, hidden: disclosureHidden } =
    useOneTimePurchaseDisclosure(orderType, consultationType);
  // The PaymentRequest handlers below are registered once per mount; they read
  // the experiment's latest resolution through this ref rather than a stale
  // closure.
  const disclosureHiddenRef = useRef(disclosureHidden);
  disclosureHiddenRef.current = disclosureHidden;

  const validationSchema = useMemo(
    () => paymentDetailsFormValidationSchema(orderType),
    [orderType],
  );

  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (paymentElementState !== "loading") {
      return undefined;
    }
    const timer = setTimeout(() => {
      setPaymentElementState((state) =>
        state === "loading" ? "timeout" : state,
      );
    }, PAYMENT_ELEMENT_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [paymentElementState]);

  // pay using apple or google or msft pay
  // Resolves true once checkout completed; see completeCheckoutAndRedirect.
  const payUsingStripePaymentRequest = async (paymentMethodID) => {
    const promoCode = getPromoCode(checkoutSessionID);
    const postData = checkoutPostData(
      getValues(),
      consultationType,
      checkoutSessionID,
      promoCode,
      { disclosureHidden: disclosureHiddenRef.current },
    );

    setFormState(getValues()); // save current form state to re-populate - call after grabbing react values

    return completeCheckoutAndRedirect({
      paymentMethodID,
      postData,
      checkoutSessionID,
      consultationType,
      history,
      setError: setMyCreditCardError,
    });
  };

  // Runs when the patient approves the Apple / Google Pay sheet.
  const handleWalletPaymentMethod = async (event) => {
    setMyCreditCardError("");
    const paymentMethodID = event.paymentMethod.id;
    let stage = "createWalletSetupIntent";
    try {
      // Each wallet approval may select a different card. Give it a fresh
      // intent so a lost response from an earlier approval cannot silently
      // select that approval's card. Leave the card form's intent untouched
      // so it remains usable if this wallet attempt fails.
      const client = KickClient.newAuthenticatedClient();
      const { data } = await client.get("/setup-intent");
      const clientSecret = data?.clientSecret;
      if (!clientSecret) {
        throw new Error("Missing client secret for wallet payment");
      }

      stage = "confirmCardSetup";
      const setupResult = await stripe.confirmCardSetup(clientSecret, {
        payment_method: paymentMethodID,
      });
      const setupError = setupResult && setupResult.error;
      if (setupError) {
        // A lost confirm response is recoverable only for the exact method
        // approved in this sheet. Never substitute a previously saved card.
        const recoveredPaymentMethodID = await recoverSucceededSetupIntent(
          stripe,
          clientSecret,
          setupError,
        );
        if (recoveredPaymentMethodID !== paymentMethodID) {
          throw setupError;
        }
      }
    } catch (error) {
      setMyCreditCardError(patientFacingStripeError(error));
      if (error?.type !== "card_error") {
        // Report only safe fields: the setup-intent request error can carry
        // authentication headers and response data. Declines stay on the page.
        bugsnagClient.notify(
          new Error(`${stage} failed: ${error?.type || "unknown"}`),
          (bugsnagEvent) => {
            bugsnagEvent.severity = "warning";
            bugsnagEvent.unhandled = false;
            bugsnagEvent.groupingHash = checkoutGroupingHash(
              stage,
              error?.type,
              error?.code,
            );
            bugsnagEvent.addMetadata("payment", {
              checkoutSessionID,
              stage,
              type: error?.type,
              code: error?.code,
              declineCode: error?.decline_code,
            });
          },
        );
      }
      event.complete("fail");
      return;
    }

    let checkoutCompleted = false;
    try {
      // A failed checkout is reported through setError and resolves falsy.
      // The sheet is still completed as "success" because the payment method
      // itself was accepted; the checkout error is already on the page.
      checkoutCompleted = await payUsingStripePaymentRequest(paymentMethodID);
      event.complete("success");
    } catch (e) {
      console.error("Error [payUsingStripePaymentRequest]: ", e);
      bugsnagClient.notify(e, (bugsnagEvent) => {
        bugsnagEvent.severity = "warning";
        bugsnagEvent.unhandled = false;
        bugsnagEvent.addMetadata("payment", {
          checkoutSessionID,
          stage: "payUsingStripePaymentRequest",
        });
      });
      event.complete("fail");
    }

    if (!checkoutCompleted) {
      // The wallet method is saved. Let Confirm retry checkout with it
      // without asking the patient to approve another wallet sheet.
      rememberConfirmedPaymentMethod(paymentMethodID);
    }
  };

  useEffect(() => {
    // hook up Stripe PaymentRequest Button for Google / Apple Pay
    // only once per mount — a second PaymentRequest while a sheet is open
    // throws InvalidAccessError "Page already has an active payment session"
    if (stripe && !paymentRequest) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "",
          amount:
            parseInt(
              orderTotalPriceWithCouponDiscount(true, checkoutSessionID),
            ) * 100 || 0, // stripe expects value in cents
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment()
        .then((result) => {
          if (result) {
            console.log("pr.canMakePayment result: ", result);
            setPaymentRequest(pr);

            pr.on("cancel", () => {
              // handle cancel event
            });

            pr.on("paymentmethod", handleWalletPaymentMethod);
          }
        })
        .catch((err) => {
          // Stripe rejects with "Frame not initialized." when the hidden
          // PaymentRequest iframe is torn down mid-flight (e.g. the component
          // unmounts during a checkout redirect). Apple/Google Pay is simply
          // unavailable in that case, so leave paymentRequest null — the wallet
          // button then doesn't render, identical to a falsy canMakePayment
          // result. Log only; do NOT notify Bugsnag, which would re-report this
          // benign, post-checkout noise as an unhandled error.
          console.warn("pr.canMakePayment() rejected: ", err);
        });
    }
  }, [stripe, paymentRequest]);

  const onSubmit = async (data) => {
    if (isChangingPaymentMethod) return;
    setFormMessage("");
    if (!hasConfirmedPaymentMethod && paymentElementState === "loading") {
      setFormMessage(PAYMENT_FORM_LOADING_MESSAGE);
      return;
    }
    try {
      await buyWithInvoice(
        data,
        stripe,
        elements,
        disclosureHiddenRef.current,
        selectedPaymentMethod,
      );
    } catch (e) {
      // Stripe.js rejects with IntegrationError when the PaymentElement iframe
      // is not mounted or not ready. It also rethrows the same error into
      // Bugsnag from its own message listener (downgraded to a handled warning
      // in stripeJsErrors.js), so no notify here; the metadata rides along on
      // that copy. Tell the patient instead of failing silently.
      console.error("buyWithInvoice error: ", e);
      bugsnagClient.addMetadata("payment", {
        checkoutSessionID,
        stage: "confirmSetup",
        paymentElementState,
      });
      setFormMessage(
        isStripeElementNotReadyError(e)
          ? PAYMENT_RETRY_MESSAGE
          : PAYMENT_GENERIC_ERROR_MESSAGE,
      );
    }
  };

  const paymentFormLoading =
    isChangingPaymentMethod ||
    (!hasConfirmedPaymentMethod && paymentElementState === "loading");
  const confirmDisabled = isSubmitting || paymentFormLoading;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ maxWidth: VISIT_MAX_WIDTH }}
    >
      <div>
        <div>
          <Controller
            render={({ field }) => (
              <MuiTextField
                {...field}
                label="Cardholder Name"
                variant="outlined"
                fullWidth
                autoFocus
              />
            )}
            name="cardName"
            control={control}
            defaultValue={cardName}
          />

          <p style={{ color: "red" }}>{errors.cardName?.message}</p>

          {notice && (
            <p
              role="status"
              style={{
                backgroundColor: kickLightBlue,
                color: kickNavy,
                borderRadius: "4px",
                padding: "12px 16px",
                margin: "0 0 16px",
                fontSize: "16px",
              }}
            >
              {notice}
            </p>
          )}

          <div
            style={{
              border: "1px solid #C4C4C4",
              borderRadius: "4px",
              margin: "6px 0 0 0",
              padding: "20px 8px",
              minWidth: "350px", // requird for mobile
            }}
          >
            {hasConfirmedPaymentMethod ? (
              <div>
                <p role="status">
                  Your payment method is saved. Confirm to finish checkout with
                  it, or choose a different payment method.
                </p>
                <Button
                  type="button"
                  disabled={isSubmitting || isChangingPaymentMethod}
                  onClick={() => {
                    setFormMessage("");
                    changePaymentMethod(getValues());
                  }}
                >
                  {isChangingPaymentMethod
                    ? "Loading payment form…"
                    : "Change payment method"}
                </Button>
              </div>
            ) : (
              <PaymentElement
                onChange={(event) =>
                  setSelectedPaymentMethod(event.value?.type || "card")
                }
                options={{
                  readOnly: isSubmitting,
                  defaultValues: {
                    billingDetails: {
                      email: getVisitEmail(),
                      name: getVisitFullName(),
                      phone: getVisitPhone(),
                    },
                  },
                }}
                onReady={() => {
                  setPaymentElementState("ready");
                  setFormMessage((message) =>
                    message === PAYMENT_FORM_LOADING_MESSAGE ? "" : message,
                  );
                }}
                onLoadError={(loadError) => {
                  setPaymentElementState("failed");
                  setFormMessage(PAYMENT_FORM_LOAD_FAILED_MESSAGE);
                  bugsnagClient.notify(
                    new Error("PaymentElement failed to load"),
                    (bugsnagEvent) => {
                      bugsnagEvent.severity = "warning";
                      bugsnagEvent.unhandled = false;
                      bugsnagEvent.groupingHash = checkoutGroupingHash(
                        "paymentElementLoad",
                        loadError?.error?.type,
                        loadError?.error?.code,
                      );
                      bugsnagEvent.addMetadata("payment", {
                        checkoutSessionID,
                        stage: "paymentElementLoad",
                        stripeError: loadError && loadError.error,
                      });
                    },
                  );
                }}
              />
            )}
          </div>
          <p
            role="alert"
            style={{
              color: "red",
              marginTop: "15px",
              marginBottom: "10px",
              fontSize: "17px",
            }}
          >
            {creditCardError}
            {formMessage}

            {!isEmpty(errors) && "Please fill out all the fields above"}
          </p>
        </div>

        <DoctorGuaranteeSection classes={classes} />

        {/* The purchase terms below say the customer is charged "the amount shown
            above", so that amount has to be on this screen, above them. It is the
            same figure the Order Details step shows as its Total. */}
        <div
          data-testid="checkout-total-due-today"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginTop: "24px",
            color: kickNavy,
            fontSize: "17px",
            fontWeight: "bold",
          }}
        >
          <span>Total due today</span>
          <span>${Number(totalDueToday).toFixed(2)}</span>
        </div>

        {/* Immediately above the pay button, before the charge — see the component.
            The checkbox is unticked by default and required by
            paymentDetailsFormValidationSchema, so the customer takes an affirmative
            action before any charge rather than being deemed to agree by paying. */}
        {disclosureReady && !disclosureHidden && (
          <Controller
            name="purchaseTermsAccepted"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <PurchaseTermsDisclosure
                orderType={orderType}
                checked={field.value}
                onChange={field.onChange}
                error={errors.purchaseTermsAccepted?.message}
              />
            )}
          />
        )}

        <Button
          type="submit"
          disabled={confirmDisabled}
          className={classes.confirmAndPayButton}
          style={{
            marginTop: "30px",
            fontSize: "18px",
            fontWeight: "bold",
            textTransform: "unset",
            backgroundColor: kickAmaOrange,
            color: kickNavy,
          }}
        >
          {paymentFormLoading
            ? "Loading payment form…"
            : selectedPaymentMethod === "klarna" && !hasConfirmedPaymentMethod
              ? "Continue to Klarna"
              : "Confirm"}
          {isSubmitting ? (
            <CircularProgress
              size="large"
              style={{
                position: "relative",
                left: "24px",
                top: "1px",
                color: kickNavy,
              }}
            />
          ) : null}
        </Button>

        {selectedPaymentMethod === "klarna" && !hasConfirmedPaymentMethod && (
          <p>
            You’ll continue to Klarna to authorize payment, then return here.
            Klarna shows the options available to you.
            {purchaseTermsVariantFor(orderType).checkboxLabel &&
              " Your membership renews automatically until you cancel."}
          </p>
        )}

        {paymentRequest && !isSubmitting && !hasConfirmedPaymentMethod && (
          <div className={classes.stripePaymentRequestButtonBlock}>
            <p style={{ textAlign: "center", margin: "0 0 10px 0" }}>— or —</p>
            <PaymentRequestButtonElement
              options={{ paymentRequest }}
              onClick={async (event) => {
                // attempt to validate form before showing paymentRequest panel
                event.preventDefault();
                const result = await trigger();
                if (result) {
                  try {
                    paymentRequest.show();
                  } catch (e) {
                    // payment sheet already open (double-click) — ignore
                  }
                }
              }}
            />
          </div>
        )}
      </div>
    </form>
  );
};

const PaymentDetails = (props) => {
  const { classes } = props;
  const { consultationType } = props.match.params;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");

  useVisitIDRequired(checkoutSessionID);
  // Browser back from Klarna can restore this page from the back/forward
  // cache with the pre-redirect form (and a spinning Confirm) still on it.
  // Reload so /setup-intent reports the abandoned attempt and it is settled.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
  if (!checkoutSessionID || !getVisitID(checkoutSessionID)) {
    window.location.href = "/me/portal";
    return null;
  }

  const [creditCardError, setMyCreditCardError] = useState("");
  // Keep the confirmed card or wallet method for checkout retries. Reset it
  // with the card form's intent when the patient chooses another method.
  const [
    { setupIntentClientSecret, confirmedPaymentMethodID },
    setPaymentSetup,
  ] = useState({ setupIntentClientSecret: null, confirmedPaymentMethodID: "" });
  const [isChangingPaymentMethod, setIsChangingPaymentMethod] = useState(false);
  const [setupLoadError, setSetupLoadError] = useState("");
  const [setupLoadRetry, setSetupLoadRetry] = useState(0);
  const [{ cardName }, setFormState] = useState({
    cardName: getVisitFullName(),
  });
  const orderType = getVisitProduct(checkoutSessionID);
  // Which arm of the checkout-klarna experiment this checkout is in. It is decided
  // BEFORE /setup-intent is asked for: the Payment Element can only show the
  // methods the SetupIntent was created with, so the arm travels on that request
  // and the server offers Klarna to the test arm only (its switch and the product
  // rules still apply). An ineligible checkout, the holdout and a slow flag
  // service all read as control -- see shared/experiments/checkout/klarnaCheckout.js.
  const { ready: klarnaDecided, offerKlarna } = useKlarnaCheckoutExperiment({
    checkoutSessionID,
    consultationType,
    orderType,
  });
  // A Klarna attempt to settle before the form may load. KLARNA_RETURN_MODE
  // right after confirmSetup (an authorization may be landing);
  // KLARNA_RESUME_MODE when /setup-intent reports one the patient walked away
  // from (browser back, reopened tab, change payment method): that one is
  // canceled after a Stripe-verified check and the form returns with a notice.
  const [pendingAttempt, setPendingAttempt] = useState(null);
  const [klarnaNotice, setKlarnaNotice] = useState(props.klarnaNotice || "");
  // The route may hand over the notice after this form has mounted.
  useEffect(() => {
    if (props.klarnaNotice) setKlarnaNotice(props.klarnaNotice);
  }, [props.klarnaNotice]);
  const resolvePendingAttempt = useCallback(({ notice }) => {
    setPendingAttempt(null);
    setKlarnaNotice(notice);
    setMyCreditCardError("");
  }, []);
  // Send checkout context for every product. The server decides which payment
  // methods to offer; the browser must not hide Klarna from one-time orders. The
  // experiment arm rides along so that only the test arm is offered Klarna.
  const fetchSetupIntent = (client) =>
    client.get("/setup-intent", {
      params: {
        visitID: getVisitID(checkoutSessionID),
        checkoutSessionID,
        consultationType,
        orderType,
        klarnaVariant: offerKlarna ? VARIANT_TEST : VARIANT_CONTROL,
      },
    });

  // grab setup intent client secret from server
  useEffect(() => {
    if (pendingAttempt) return;
    // https://devtrium.com/posts/async-functions-useeffect
    if (getOrderComplete(checkoutSessionID)) {
      // if order is complete, don't let user go back to payment page
      props.history.push(
        `/visits/${consultationType}/payment/complete?checkoutSessionID=${checkoutSessionID}`,
      );
      window.location.href = `/visits/${consultationType}/payment/complete?checkoutSessionID=${checkoutSessionID}`;
    }
    console.log(
      "getVisitDifferentPharmacy: ",
      getVisitDifferentPharmacy(checkoutSessionID),
    );

    const fetchData = async () => {
      // get the client secret from server
      const client = KickClient.newAuthenticatedClient();
      const setupIntentResult = await fetchSetupIntent(client);

      if (setupIntentResult.data.pendingAttemptID) {
        setPendingAttempt({
          id: setupIntentResult.data.pendingAttemptID,
          mode: KLARNA_RESUME_MODE,
        });
        return;
      }

      if (!setupIntentResult.data?.clientSecret) {
        throw new Error("Missing client secret for payment form");
      }

      setPaymentSetup({
        setupIntentClientSecret: setupIntentResult.data,
        confirmedPaymentMethodID: "",
      });
    };

    // The intent is created with or without Klarna, so wait for the arm first.
    if (!setupIntentClientSecret && klarnaDecided) {
      setSetupLoadError("");
      fetchData().catch((error) => {
        // Never log an Axios response: setup responses contain client secrets.
        const message = error.response?.data?.error;
        setSetupLoadError(
          typeof message === "string"
            ? message
            : "We couldn't load the payment form. Please try again.",
        );
      });
    }

    if (typeof window.Beacon == "function") {
      // window.Beacon("init", "e67f20b1-8927-4c18-b2a9-3e8119a361ad");
    }
  }, [
    checkoutSessionID,
    consultationType,
    setupIntentClientSecret,
    pendingAttempt,
    klarnaDecided,
    setupLoadRetry,
  ]);

  const changePaymentMethod = async (values) => {
    setIsChangingPaymentMethod(true);
    setMyCreditCardError("");
    try {
      const client = KickClient.newAuthenticatedClient();
      const { data } = await fetchSetupIntent(client);
      if (data?.pendingAttemptID) {
        setPendingAttempt({
          id: data.pendingAttemptID,
          mode: KLARNA_RESUME_MODE,
        });
        return;
      }
      if (!data?.clientSecret) {
        throw new Error("Missing client secret for replacement payment form");
      }
      setFormState(values);
      setPaymentSetup({
        setupIntentClientSecret: data,
        confirmedPaymentMethodID: "",
      });
    } catch (error) {
      // Keep the saved method usable if the new form cannot be loaded.
      console.error("changePaymentMethod failed: ", error);
      setMyCreditCardError(
        "We couldn't load a new payment form. Try again, or confirm with your saved payment method.",
      );
    } finally {
      setIsChangingPaymentMethod(false);
    }
  };

  // A SetupIntent can be confirmed once. Whichever path confirmed it (card form
  // or wallet button), keep the method so a retry reuses it instead of
  // confirming the same intent again.
  const rememberConfirmedPaymentMethod = (paymentMethodID) => {
    setPaymentSetup((current) => ({
      ...current,
      confirmedPaymentMethodID: paymentMethodID,
    }));
  };

  // buyWithInvoice handles both subscription and non-subscription billing
  // disclosureHidden is resolved in BillingForm (where the panel renders) and passed
  // down, so the recorded terms version always matches what that customer was shown.
  const buyWithInvoice = async (
    values,
    stripe,
    elements,
    disclosureHidden,
    selectedPaymentMethod,
  ) => {
    setMyCreditCardError("");
    const promoCode = getPromoCode(checkoutSessionID);
    const postData = checkoutPostData(
      values,
      consultationType,
      checkoutSessionID,
      promoCode,
      { disclosureHidden },
    );

    if (
      selectedPaymentMethod === "klarna" &&
      setupIntentClientSecret.klarnaEnabled
    ) {
      if (!postData) return false;
      if (
        purchaseTermsVariantFor(orderType).checkboxLabel &&
        !values.purchaseTermsAccepted
      )
        return false;
      let attemptID;
      try {
        const submitted = await elements.submit();
        if (submitted.error) {
          setMyCreditCardError(submitted.error.message);
          return false;
        }
        // This async preparation is exclusive to Klarna. Card/Apple Pay must
        // still reach confirmSetup synchronously from the Confirm click.
        postData.deviceFingerprint = await computeDeviceFingerprint();
        const client = KickClient.newAuthenticatedClient();
        const { data } = await client.post("/klarna/checkout-attempts", {
          setupIntentID: setupIntentClientSecret.setupIntentID,
          checkoutSessionID,
          termsAccepted: true,
          checkout: postData,
        });
        attemptID = data.attemptID;
        if (!attemptID) throw new Error("Missing checkout attempt");
        const { error } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: klarnaReturnURL(
              consultationType,
              checkoutSessionID,
              attemptID,
            ),
            mandate_data: {
              customer_acceptance: {
                type: "online",
                online: { infer_from_client: true },
              },
            },
          },
          redirect: "if_required",
        });
        if (error) {
          // Confirmation can succeed despite a lost response. Cancellation
          // reconciles Stripe first and returns the completed order if so.
          const canceled = await client.post(
            `/klarna/checkout-attempts/${attemptID}/cancel`,
            {},
          );
          if (finishKlarnaCheckout(canceled.data, props.history)) return true;
          setFormState(values);
          setMyCreditCardError(
            "Klarna wasn't completed. Please choose a payment method to try again.",
          );
          setPaymentSetup({
            setupIntentClientSecret: null,
            confirmedPaymentMethodID: "",
          });
          return false;
        }
        setPendingAttempt({ id: attemptID, mode: KLARNA_RETURN_MODE });
      } catch (_) {
        // Axios and Stripe errors can contain the request payload/client secret.
        // Never log them. Reloading setup also discovers a persisted attempt
        // when the prepare response was lost before we learned its ID.
        if (attemptID) {
          setPendingAttempt({ id: attemptID, mode: KLARNA_RETURN_MODE });
        } else {
          setMyCreditCardError("We couldn't prepare Klarna. Please try again.");
          setPaymentSetup({
            setupIntentClientSecret: null,
            confirmedPaymentMethodID: "",
          });
        }
      }
      return false;
    }

    // Nothing async may run between the Confirm click and stripe.confirmSetup.
    // Stripe opens the Apple Pay sheet from inside confirmSetup when the patient
    // picked Apple Pay in the PaymentElement, and Safari only allows that while the
    // click's user activation is still alive. Any macrotask boundary in between
    // (FingerprintJS yields to requestIdleCallback/setTimeout) ends it, and Stripe
    // rejects with "Failed to show the Apple Pay payment sheet". The device
    // fingerprint is computed further down, after Stripe has acted.
    //
    // A retry after /complete-checkout failed skips confirmSetup entirely: the
    // SetupIntent already succeeded, so Stripe would reject a second confirm
    // (after re-opening the Apple Pay sheet). The saved payment method is reused.
    let paymentMethodID = confirmedPaymentMethodID;
    if (!paymentMethodID) {
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: "https://www.gokick.com/",
        },
        // Uncomment below if you only want redirect for redirect-based payments
        redirect: "if_required",
      });

      if (error) {
        // just a validation error - prevent page from refreshing
        if (error.type == "validation_error") {
          console.log("just return validation error");
          return;
        }

        // The confirm may have gone through at Stripe even though this call
        // failed (lost response, or an earlier attempt already succeeded).
        paymentMethodID = await recoverSucceededSetupIntent(
          stripe,
          setupIntentClientSecret.clientSecret,
          error,
        );
      } else {
        paymentMethodID = setupIntent.payment_method;
      }

      if (!paymentMethodID) {
        setFormState(values); // save current form state to re-populate - call after grabbing react values
        setMyCreditCardError(patientFacingStripeError(error));
        if (error && error.type !== "card_error") {
          // A developer-facing failure (invalid_request_error, api_error, the Apple
          // Pay sheet not opening). Keep it observable as a handled warning now that
          // Stripe's own "Frame not initialized." noise is filtered out.
          bugsnagClient.notify(
            new Error(`confirmSetup failed: ${error.type}`),
            (bugsnagEvent) => {
              bugsnagEvent.severity = "warning";
              bugsnagEvent.unhandled = false;
              bugsnagEvent.groupingHash = checkoutGroupingHash(
                "confirmSetup",
                error.type,
                error.code,
              );
              bugsnagEvent.addMetadata("payment", {
                checkoutSessionID,
                stage: "confirmSetup",
                type: error.type,
                code: error.code,
                declineCode: error.decline_code,
                message: error.message,
              });
            },
          );
        }
        return false;
      }
      rememberConfirmedPaymentMethod(paymentMethodID);
    }

    // The computed device fingerprint for Visa Compelling Evidence 3.0. The other two
    // identifiers (kick_did, __stripe_mid) are cookies the server reads for itself.
    // All three are stored; which is submitted is decided when a dispute arrives.
    // Computed only now, after confirmSetup, for the user-activation reason above.
    if (postData) {
      postData.deviceFingerprint = await computeDeviceFingerprint();
    }

    setFormState(values); // save current form state to re-populate - call after grabbing react values

    await completeCheckoutAndRedirect({
      paymentMethodID,
      postData,
      checkoutSessionID,
      consultationType,
      history: props.history,
      setError: setMyCreditCardError,
    });
  };

  if (pendingAttempt) {
    return (
      <CheckoutInterstitial
        classes={classes}
        checkoutSessionID={checkoutSessionID}
      >
        <KlarnaCheckoutReturn
          attemptID={pendingAttempt.id}
          mode={pendingAttempt.mode}
          history={props.history}
          onResolved={resolvePendingAttempt}
        />
      </CheckoutInterstitial>
    );
  }

  // The form's own loading and load-error states wear the same chrome and
  // card as a Klarna settle screen, so the page never shows bare text.
  if (!setupIntentClientSecret) {
    return (
      <CheckoutInterstitial
        classes={classes}
        checkoutSessionID={checkoutSessionID}
      >
        {setupLoadError ? (
          <CheckoutStatusCard
            title="We hit a snag"
            message={setupLoadError}
            messageRole="alert"
            actions={
              <StatusButton
                onClick={() => setSetupLoadRetry((count) => count + 1)}
              >
                Try again
              </StatusButton>
            }
          />
        ) : (
          <CheckoutStatusCard
            spinner
            title="Loading your payment options"
            message="Just a moment…"
          />
        )}
      </CheckoutInterstitial>
    );
  }

  return (
    <Elements
      // Stripe's clientSecret is immutable; a replacement needs new Elements
      // and wallet handlers, with the PaymentElement readiness gate reset.
      key={setupIntentClientSecret.clientSecret}
      stripe={getStripe()}
      options={getStripeOptions(setupIntentClientSecret)}
    >
      <div className={`${classes.containerWithSpacing} payment-section`}>
        <div className={classes.checkoutProgressMobile}>
          <CheckoutProgress step={2} checkoutSessionID={checkoutSessionID} />
        </div>
        <GridContainer>
          <GridItem xs={12} md={4}>
            <SocialProof
              style={{ borderBottom: "1px solid #ddd", marginBottom: "24px" }}
              consultationType={consultationType}
            />
            {isConsultationTypeInsomniaInitial(consultationType) && (
              <>
                <WhatHappensNext />
                <SleepConciergeInfo />
                <SelectedSleepMeds />
              </>
            )}
          </GridItem>
          <GridItem xs={12} md={8}>
            <div className={classes.paymentForm}>
              <div className={classes.checkoutProgressDesktop}>
                <CheckoutProgress
                  step={2}
                  checkoutSessionID={checkoutSessionID}
                />
              </div>
              <BillingForm
                classes={classes}
                checkoutSessionID={checkoutSessionID}
                consultationType={consultationType}
                cardName={cardName}
                creditCardError={creditCardError}
                notice={klarnaNotice}
                history={props.history}
                buyWithInvoice={buyWithInvoice}
                hasConfirmedPaymentMethod={!!confirmedPaymentMethodID}
                isChangingPaymentMethod={isChangingPaymentMethod}
                changePaymentMethod={changePaymentMethod}
                rememberConfirmedPaymentMethod={rememberConfirmedPaymentMethod}
                setFormState={setFormState}
                setMyCreditCardError={setMyCreditCardError}
              />
            </div>
          </GridItem>
        </GridContainer>
      </div>
    </Elements>
  );
};

// Every screen shown in place of the payment form (loading it, a load error,
// a Klarna settle screen) wears the form's chrome: same container, progress
// indicator and width, so a detour never looks like a different site. The
// progress bar reads checkout cookies, so it is skipped when browser storage
// was lost (the Route path below can still reconcile).
export function CheckoutInterstitial({ classes, checkoutSessionID, children }) {
  const progress = getVisitID(checkoutSessionID) ? (
    <CheckoutProgress step={2} checkoutSessionID={checkoutSessionID} />
  ) : null;
  return (
    <div className={`${classes.containerWithSpacing} payment-section`}>
      <div className={classes.checkoutProgressMobile}>{progress}</div>
      <div
        className={classes.paymentForm}
        style={{ width: "100%", margin: "0 auto", padding: "0 16px" }}
      >
        <div className={classes.checkoutProgressDesktop}>{progress}</div>
        {children}
      </div>
    </div>
  );
}

// Handle a Klarna redirect before the legacy local-storage visit guard. A
// patient who lost browser storage can still recover their authenticated
// server submission. Once the attempt is settled without a completed order the
// form loads in place, with the reason shown above it; the attempt id leaves
// the URL so a refresh shows the form rather than this screen again.
export function PaymentDetailsRoute(props) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const attemptID = query.get("klarnaAttempt");
  const checkoutSessionID = query.get("checkoutSessionID");
  const { consultationType } = props.match.params;
  const { history } = props;
  const [settled, setSettled] = useState(null);
  const onResolved = useCallback(
    ({ notice }) => {
      if (!getVisitID(checkoutSessionID)) {
        window.location.assign("/me/portal");
        return;
      }
      // Store the notice BEFORE rewriting the URL. React 17 renders each of
      // these updates on its own: the history change re-renders this route with
      // no attempt id, so the form must already have its notice when it mounts,
      // or a declined Klarna return lands on a form with no explanation.
      setSettled({ notice });
      history.replace(
        `/visits/${encodeURIComponent(consultationType)}/payment/payment-details?checkoutSessionID=${encodeURIComponent(checkoutSessionID)}`,
      );
    },
    [checkoutSessionID, consultationType, history],
  );
  if (attemptID && !settled) {
    return (
      <CheckoutInterstitial
        classes={props.classes}
        checkoutSessionID={checkoutSessionID}
      >
        <KlarnaCheckoutReturn
          attemptID={attemptID}
          mode={KLARNA_RETURN_MODE}
          history={history}
          onResolved={onResolved}
        />
      </CheckoutInterstitial>
    );
  }
  return <PaymentDetails {...props} klarnaNotice={settled?.notice || ""} />;
}

export default withStyles(consultationStyles)(PaymentDetailsRoute);
